import { PubSubService } from './_shared/pubSubServiceInterface';

interface ModalityFavorites {
  [modality: string]: string[];
}

const EVENTS = {
  FAVORITES_UPDATED: 'event::favoritesService:favoritesUpdated',
  MODALITY_CHANGED: 'event::favoritesService:modalityChanged',
};

export default class FavoritesService extends PubSubService {
  public static REGISTRATION = {
    name: 'favoritesService',
    create: ({ servicesManager }) => {
      return new FavoritesService(servicesManager);
    },
  };

  private _modalityFavorites: ModalityFavorites = {};
  private _currentModality: string = 'DEFAULT';
  private _storageKey = 'ohif-favorite-tools-by-modality';
  private _servicesManager: any;

  constructor(servicesManager: any) {
    super(EVENTS); // Pass EVENTS to parent constructor
    this._servicesManager = servicesManager;
    this._loadFromStorage();
    this._subscribeToModalityChanges();
  }

  public get events() {
    return EVENTS;
  }

  /**
   * Initialize toolbar integration - kept for backward compatibility with mode initialization
   * This is now a no-op since we render favorites in the sidebar instead of styling toolbar buttons
   */
  public initializeToolbarIntegration(modality?: string): void {
    // No-op: Favorites are now rendered in sidebar, not as toolbar button styling
    // This method is kept to prevent breaking mode initialization code
  }

  private _subscribeToModalityChanges(): void {
    // Listen for viewport changes to detect modality
    const { viewportGridService } = this._servicesManager.services;

    if (viewportGridService) {
      viewportGridService.subscribe(
        viewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED,
        this._handleViewportChange.bind(this)
      );
    }
  }

  private _handleViewportChange(): void {
    // Get current modality from active viewport
    const modality = this._getCurrentModality();
    if (modality && modality !== this._currentModality) {
      const previousModality = this._currentModality;
      this._currentModality = modality;

      this._broadcastEvent(EVENTS.MODALITY_CHANGED, {
        modality,
        previousModality,
        favorites: this.getFavoriteTools(modality),
      });
    }
  }

  private _getCurrentModality(): string {
    try {
      const { viewportGridService, displaySetService } = this._servicesManager.services;
      const activeViewportId = viewportGridService?.getActiveViewportId();

      if (activeViewportId) {
        const viewport = viewportGridService.getViewportInfo(activeViewportId);
        const displaySetInstanceUIDs = viewport?.getDisplaySetInstanceUIDs?.();

        if (displaySetInstanceUIDs?.length > 0) {
          const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUIDs[0]);
          return displaySet?.Modality || 'DEFAULT';
        }
      }
    } catch (error) {
      console.warn('Could not determine current modality:', error);
    }

    return 'DEFAULT';
  }

  private _loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this._storageKey);
      if (stored) {
        this._modalityFavorites = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load favorites from storage:', error);
      this._modalityFavorites = {};
    }
  }

  private _saveToStorage(): void {
    try {
      localStorage.setItem(this._storageKey, JSON.stringify(this._modalityFavorites));
    } catch (error) {
      console.warn('Failed to save favorites to storage:', error);
    }
  }


  public getFavoriteTools(modality?: string): string[] {
    const targetModality = modality || this._currentModality;
    return [...(this._modalityFavorites[targetModality] || this._modalityFavorites['DEFAULT'] || [])];
  }

  public setModalityFavorites(modalityFavorites: ModalityFavorites): void {
    this._modalityFavorites = { ...modalityFavorites };
    this._saveToStorage();
    this._broadcastEvent(EVENTS.FAVORITES_UPDATED, {
      modality: this._currentModality,
      favorites: this.getFavoriteTools(),
      allModalityFavorites: this._modalityFavorites,
    });
  }

  public setFavoriteTools(toolIds: string[], modality?: string): void {
    const targetModality = modality || this._currentModality;
    this._modalityFavorites[targetModality] = [...toolIds];
    this._saveToStorage();
    this._broadcastEvent(EVENTS.FAVORITES_UPDATED, {
      modality: targetModality,
      favorites: toolIds,
      allModalityFavorites: this._modalityFavorites,
    });
  }

  public addToFavorites(toolId: string, modality?: string): boolean {
    const targetModality = modality || this._currentModality;
    const currentFavorites = this._modalityFavorites[targetModality] || [];

    if (!currentFavorites.includes(toolId)) {
      this._modalityFavorites[targetModality] = [...currentFavorites, toolId];
      this._saveToStorage();
      this._broadcastEvent(EVENTS.FAVORITES_UPDATED, {
        modality: targetModality,
        favorites: this._modalityFavorites[targetModality],
        allModalityFavorites: this._modalityFavorites,
        added: toolId,
      });
      return true;
    }
    return false;
  }

  public removeFromFavorites(toolId: string, modality?: string): boolean {
    const targetModality = modality || this._currentModality;
    const currentFavorites = this._modalityFavorites[targetModality] || [];
    const index = currentFavorites.indexOf(toolId);

    if (index > -1) {
      this._modalityFavorites[targetModality] = currentFavorites.filter(id => id !== toolId);
      this._saveToStorage();
      this._broadcastEvent(EVENTS.FAVORITES_UPDATED, {
        modality: targetModality,
        favorites: this._modalityFavorites[targetModality],
        allModalityFavorites: this._modalityFavorites,
        removed: toolId,
      });
      return true;
    }
    return false;
  }

  /**
   * Toggle a tool's favorite status
   */
  public toggleFavorite(toolId: string, modality?: string): boolean {
    if (this.isFavorite(toolId, modality)) {
      return this.removeFromFavorites(toolId, modality);
    } else {
      return this.addToFavorites(toolId, modality);
    }
  }

  public isFavorite(toolId: string, modality?: string): boolean {
    const targetModality = modality || this._currentModality;
    const favorites = this._modalityFavorites[targetModality] || this._modalityFavorites['DEFAULT'] || [];
    return favorites.includes(toolId);
  }

  public clearFavorites(modality?: string): void {
    const targetModality = modality || this._currentModality;
    this._modalityFavorites[targetModality] = [];
    this._saveToStorage();
    this._broadcastEvent(EVENTS.FAVORITES_UPDATED, {
      modality: targetModality,
      favorites: [],
      allModalityFavorites: this._modalityFavorites,
      cleared: true,
    });
  }

  public getAllModalityFavorites(): ModalityFavorites {
    return { ...this._modalityFavorites };
  }

  public getCurrentModality(): string {
    return this._currentModality;
  }

  public setCurrentModality(modality: string): void {
    if (modality !== this._currentModality) {
      const previousModality = this._currentModality;
      this._currentModality = modality;
      this._broadcastEvent(EVENTS.MODALITY_CHANGED, {
        modality,
        previousModality,
        favorites: this.getFavoriteTools(modality),
      });
    }
  }

  /**
   * Get all available tool buttons from toolbar service
   */
  public getAvailableTools(): string[] {
    const toolbarService = this._servicesManager?.services?.toolbarService;
    if (!toolbarService) {
      return [];
    }

    const buttons = toolbarService.getButtons();
    return Object.keys(buttons).filter(buttonId => {
      const button = buttons[buttonId];
      // Filter out section buttons and other non-tool buttons
      return button?.uiType === 'ohif.toolButton' && !button?.props?.buttonSection;
    });
  }

  /**
   * Get favorite tools that are currently available in the toolbar
   */
  public getAvailableFavoriteTools(modality?: string): string[] {
    const favorites = this.getFavoriteTools(modality);
    const availableTools = this.getAvailableTools();
    return favorites.filter(toolId => availableTools.includes(toolId));
  }


  /**
   * Reset all favorites to default for a modality
   */
  public resetToDefaults(modality?: string): void {
    const targetModality = modality || this._currentModality;

    // Define default favorites per modality
    const defaults = {
      'DEFAULT': ['Zoom', 'Pan', 'Length', 'WindowLevel'],
      'CT': ['Zoom', 'Pan', 'WindowLevel', 'EllipticalROI', 'ReferenceLines'],
      'MR': ['Zoom', 'Pan', 'WindowLevel', 'EllipticalROI', 'StackScroll'],
      'DX': ['Zoom', 'Pan', 'Length', 'Angle', 'invert'],
      'CR': ['Zoom', 'Pan', 'Length', 'Angle', 'invert'],
      'US': ['Zoom', 'Pan', 'UltrasoundDirectionalTool', 'Cine'],
      'MG': ['Zoom', 'Pan', 'Length', 'Angle', 'Magnify'],
      'PT': ['Zoom', 'Pan', 'WindowLevel', 'EllipticalROI', 'Probe'],
    };

    const defaultFavorites = defaults[targetModality] || defaults['DEFAULT'];
    this.setFavoriteTools(defaultFavorites, targetModality);
  }

  /**
   * Cleanup method for mode exit
   */
  public destroy(): void {
    // Clear subscriptions
    this.unsubscriptions.forEach(unsub => unsub());
    this.unsubscriptions = [];
  }
}
